// hemo's ISLES'24 comparison: a hemo-sim-v0 output of kind "comparison" (pipelines/sim/hemo/isles/
// export.py) that scores the stroke model's tissue runs against real patients. Its per-case table has
// a row per patient × predictor × target; the app reads the model's infarct against each matched
// patient's follow-up infarct, both measured on the patient's own CT grid, and the admission core's
// against the same infarct: the trivial prediction the model must beat.

import { SimFiles, type SimSource } from './files.ts';
import type { CommonHeader } from './types.ts';
import { noInfarct, type IslesScored } from '../isles-v5.ts';

/** One ISLES'24 patient, matched to a tissue run. */
export interface IslesCase {
  case: string;
  /** The run the patient was matched to: clot site (l-m1, l-m1-distal, l-ica), collateral grade and
   *  reperfusion arm (untreated, 1h, 3h, 6h). */
  site: string;
  grade: string;
  treatment: string;
  /** The follow-up infarct and the model's, mL, over the region they were scored in. */
  measuredMl: number;
  modelMl: number;
  dice: number | null;
  /** The admission CT-perfusion core (predictor `core`) against the same infarct; null where the table
   *  has no row for the patient. */
  core: { ml: number; dice: number | null } | null;
}

export interface IslesComparison {
  path: string;
  header: CommonHeader;
  cases: IslesCase[];
}

/** A CSV's rows as string records. hemo's comparison tables have no quoted cells. */
export function csvRecords(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const head = lines.shift()?.split(',').map((s) => s.trim()) ?? [];
  return lines.map((line) => {
    const cells = line.split(',');
    return Object.fromEntries(head.map((h, i) => [h, cells[i]?.trim() ?? '']));
  });
}

const num = (v: string | undefined) => (v === undefined || v === '' ? NaN : Number(v));

const finite = (v: number) => (Number.isFinite(v) ? v : null);

/** The patients the model's infarct was scored on (predictor `model-infarct`, target `final infarct`),
 *  each with its admission core's row (`core`, the same target). */
export function islesCases(rows: Record<string, string>[]): IslesCase[] {
  const infarct = rows.filter((r) => r.target === 'final infarct');
  const core = new Map(infarct.filter((r) => r.predictor === 'core' && Number.isFinite(num(r.pred_ml))).map((r) => [r.case ?? '', { ml: num(r.pred_ml), dice: finite(num(r.dice)) }]));
  return infarct
    .filter((r) => r.predictor === 'model-infarct')
    .map((r) => ({
      case: r.case ?? '',
      site: r.run_occlusion ?? '',
      grade: r.grade ?? '',
      treatment: r.treatment ?? '',
      measuredMl: num(r.true_ml),
      modelMl: num(r.pred_ml),
      dice: finite(num(r.dice)),
      core: core.get(r.case ?? '') ?? null,
    }))
    .filter((c) => c.case && Number.isFinite(c.measuredMl) && Number.isFinite(c.modelMl));
}

/** A patient's three predictions, as the panel and Methods set them side by side. */
export function scoredOf(c: IslesCase): IslesScored {
  const of = (ml: number, dice: number | null) => ({ ml, errMl: Math.abs(ml - c.measuredMl), dice: dice ?? NaN });
  return { measuredMl: c.measuredMl, model: of(c.modelMl, c.dice), zero: noInfarct(c.measuredMl), core: c.core ? of(c.core.ml, c.core.dice) : { ml: NaN, errMl: NaN, dice: NaN } };
}

/** The first of these comparison exports that exists, with its patients; null when none does. */
export async function loadIsles(src: SimSource, candidates: string[]): Promise<IslesComparison | null> {
  for (const path of candidates) {
    const files = new SimFiles(src, path);
    const header = await files.maybeJson<CommonHeader>('header.json');
    if (!header) continue;
    if (header.kind !== 'comparison') throw new Error(`sims/${path}: a ${header.kind} output, not a comparison`);
    const listed = header.files?.tables;
    const tables = Array.isArray(listed) ? listed.filter((t): t is string => typeof t === 'string') : [];
    const perCase = tables.find((t) => /(^|\/)per_case\.csv$/.test(t)) ?? 'metrics/per_case.csv';
    return { path, header, cases: islesCases(csvRecords(await files.text(perCase))) };
  }
  return null;
}
